/**
 * Chatter API (R18f)
 *
 * Typed client for `/api/chatters` — the durable per-chatter store described in
 * `architecture/chatter-persistence.md`. Three calls: a paged list, a single
 * chatter, and the preferences replace.
 *
 * WHY THIS FILE EXISTS, given that R18c shipped without it. `ChatterPanel`
 * built its URL inline with `api.get`, which was fine while exactly one
 * component read exactly one endpoint. R18f adds a second reader and a second
 * endpoint, and overlays are expected to want the same data later — at which
 * point "the chatter URL" would be hand-assembled in three places with three
 * chances to forget `encodeURIComponent` on a value that comes off the wire.
 *
 * TWO CONTRACTS FROM THE BACKEND ARE MIRRORED HERE AND MUST NOT BE SMOOTHED
 * OVER BY A CALLER:
 *
 * 1. `message_total` is `number | null`, and NULL IS NOT ZERO. Null means the
 *    chatter has never been counted and flushed; zero means a flush ran and
 *    counted nothing. That distinction is what tells a working write-behind
 *    flush from a silent one, and `?? 0` anywhere in a renderer destroys it for
 *    every row at once. Same rule `ChatterPanel` already follows for
 *    `activity_local`.
 *
 * 2. `next_cursor` is OPAQUE. Pass it back verbatim. It encodes the sort and a
 *    snapshot instant, so `sort` must be resent unchanged alongside it or the
 *    server returns 400; `q` and `limit` are not encoded and must be resent
 *    too. Do not parse it, do not build one, and do not fall back to page one
 *    when the server rejects it — a silent restart hands the caller a duplicate
 *    page with no signal that it had happened.
 */

import api from '@/api/client'

/** Orderings the list endpoint accepts. Direction is fixed server-side. */
export type ChatterSort = 'last_seen' | 'first_seen' | 'login'

/** One chatter as `GET /api/chatters/{channel_id}/{twitch_user_id}` returns it. */
export interface ChatterDetail {
  channel_id: number
  twitch_user_id: string
  login: string | null
  display_name: string | null
  first_seen_at: string
  last_seen_at: string
  last_greeted_at: string | null
  /**
   * The STORED array, still in UTC. Carried here unused so the conversion
   * stays checkable by hand against `activity_local` — this class of error
   * renders a plausible graph rather than raising, so a second opinion on the
   * wire is worth its bytes.
   */
  activity: number[] | null
  preferences: Record<string, unknown> | null
  timezone: string
  /** False when UTC is a fallback rather than the channel's choice. */
  timezone_is_set: boolean
  utc_offset_minutes: number
  /** Minutes past the hour each column STARTS. 30 for Adelaide, 45 for Kathmandu. */
  bucket_label_minutes: number
  /** `activity` rotated into local hours. Null exactly when `activity` is null. */
  activity_local: number[] | null
}

/**
 * One chatter as the LIST endpoint returns them — a strict subset of
 * `ChatterDetail`, with no arrays at all. The histogram is detail-only: fifty
 * rows would otherwise ship fifty 168-int arrays to render a name and a date.
 */
export interface ChatterListItem {
  twitch_user_id: string
  login: string | null
  display_name: string | null
  first_seen_at: string
  last_seen_at: string
  last_greeted_at: string | null
  /** Sum of the 168 buckets, or NULL for never counted. **Null is not zero.** */
  message_total: number | null
}

export interface ChatterListResponse {
  items: ChatterListItem[]
  /** Opaque. Null means this was the last page. */
  next_cursor: string | null
  /** Returned on the first page only; null on every page after it. */
  total_count: number | null
}

export interface ListChattersParams {
  /** Case-insensitive substring of login OR display name. */
  q?: string
  sort?: ChatterSort
  limit?: number
  /** Verbatim `next_cursor` from the previous page. */
  cursor?: string
}

/** Page through a channel's chatters. */
export function listChatters(
  channelId: number,
  params: ListChattersParams = {},
): Promise<ChatterListResponse> {
  const search = new URLSearchParams()
  if (params.q) search.set('q', params.q)
  if (params.sort) search.set('sort', params.sort)
  if (params.limit) search.set('limit', String(params.limit))
  if (params.cursor) search.set('cursor', params.cursor)
  const qs = search.toString()
  return api.get<ChatterListResponse>(
    `/api/chatters/${channelId}${qs ? `?${qs}` : ''}`,
  )
}

/**
 * One chatter, including the activity histogram and the localisation block.
 *
 * A 404 here is ordinary, not broken: the row is created by the bot on a
 * chatter's first line and this API never creates one.
 */
export function getChatter(
  channelId: number,
  twitchUserId: string,
): Promise<ChatterDetail> {
  return api.get<ChatterDetail>(
    `/api/chatters/${channelId}/${encodeURIComponent(twitchUserId)}`,
  )
}

/**
 * Replace a chatter's preferences blob outright and get the updated row back.
 *
 * REPLACES — it does not deep-merge, and null is not a delete. To change one
 * key, read the object, edit it, and send the whole thing back. Send `{}` to
 * clear. The serialised blob is capped server-side at 8 KB (413 past that).
 */
export function updateChatterPreferences(
  channelId: number,
  twitchUserId: string,
  preferences: Record<string, unknown>,
): Promise<ChatterDetail> {
  return api.patch<ChatterDetail>(
    `/api/chatters/${channelId}/${encodeURIComponent(twitchUserId)}/preferences`,
    { preferences },
  )
}
